import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Mail, PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/site";

const SEEN_KEY = "tbe-welcomed";

export default function WelcomePopup() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("invite");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [highlights, setHighlights] = useState([]);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setOpen(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = (v) => {
    if (!v) {
      localStorage.setItem(SEEN_KEY, "1");
      setOpen(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email, name: "", source: "welcome-popup" });
      localStorage.setItem(SEEN_KEY, "1");
      try {
        const r = await axios.get(`${API}/events`);
        const today = new Date().toISOString().slice(0, 10);
        setHighlights(
          (r.data || [])
            .filter((ev) => (ev.sort || "") >= today && !ev.calendar_only)
            .sort((a, b) => (a.sort || "").localeCompare(b.sort || ""))
            .slice(0, 3)
        );
      } catch {
        setHighlights([]);
      }
      setStage("thanks");
    } catch {
      toast.error("Something went wrong", { description: "Please try again or use the footer signup." });
    } finally {
      setSending(false);
    }
  };

  const planVisit = () => {
    dismiss(false);
    navigate("/contact");
  };

  return (
    <Dialog open={open} onOpenChange={dismiss}>
      <DialogContent data-testid="welcome-popup" className="max-w-md border-gold/40 bg-inkdeep p-0 overflow-hidden">
        {stage === "invite" ? (
          <div className="p-8 md:p-10">
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-4">Beth El Center for Jewish Life</p>
            <DialogTitle asChild>
              <h2 className="font-syne font-extrabold uppercase tracking-tighter leading-[0.95] text-2xl md:text-3xl mb-4 text-bone">
                Shalom &amp; <span className="text-gold">welcome!</span>
              </h2>
            </DialogTitle>
            <p className="font-cormorant italic text-lg text-foreground/70 mb-7">
              A warm Jewish home in West Palm Beach for 100 years — and there's a seat here for you.
            </p>
            <form onSubmit={subscribe} className="mb-4">
              <label htmlFor="welcome-email" className="block text-[10px] tracking-[0.25em] uppercase text-foreground/50 mb-2">
                Get our newsletter — events, services &amp; celebrations
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground/40" aria-hidden="true" />
                  <input
                    id="welcome-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="welcome-popup-email-input"
                    className="w-full bg-ink border border-white/15 focus:border-gold/60 outline-none pl-10 pr-3 py-3 text-sm placeholder:text-foreground/35 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="welcome-popup-subscribe-button"
                  className="px-5 py-3 bg-gold text-inkdeep text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors disabled:opacity-60"
                >
                  {sending ? "…" : "Join"}
                </button>
              </div>
            </form>
            <div className="flex items-center gap-4">
              <button
                onClick={planVisit}
                data-testid="welcome-popup-visit-button"
                className="px-5 py-3 border border-gold/50 text-gold text-[11px] tracking-[0.2em] uppercase hover:bg-gold hover:text-inkdeep transition-colors"
              >
                Plan Your Visit
              </button>
              <button
                onClick={() => dismiss(false)}
                data-testid="welcome-popup-dismiss-button"
                className="text-[11px] tracking-[0.2em] uppercase text-foreground/40 hover:text-foreground transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 md:p-10" data-testid="welcome-popup-thanks">
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-4 inline-flex items-center gap-2">
              <PartyPopper size={13} aria-hidden="true" /> You're on the list
            </p>
            <DialogTitle asChild>
              <h2 className="font-syne font-extrabold uppercase tracking-tighter leading-[0.95] text-2xl md:text-3xl mb-4 text-bone">
                Welcome to the <span className="text-gold">family!</span>
              </h2>
            </DialogTitle>
            <p className="font-cormorant italic text-lg text-foreground/70 mb-6">
              A welcome email is on its way. Here's what's coming up at Beth El:
            </p>
            {highlights.length > 0 ? (
              <div className="space-y-3 mb-7">
                {highlights.map((ev) => (
                  <div key={ev.id} className="border border-white/10 px-4 py-3 flex items-start gap-3" data-testid={`welcome-highlight-${ev.id}`}>
                    <CalendarDays size={14} className="text-gold shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="font-syne font-bold text-sm leading-snug">{ev.title}</p>
                      <p className="text-xs text-foreground/55 mt-0.5">
                        {[ev.weekday, ev.month, ev.day].filter(Boolean).join(" ")} · {ev.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-foreground/55 mb-7">Check the calendar for services, classes and celebrations.</p>
            )}
            <div className="flex items-center gap-4">
              <button
                onClick={planVisit}
                data-testid="welcome-popup-thanks-visit-button"
                className="px-5 py-3 bg-gold text-inkdeep text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors"
              >
                Plan Your Visit
              </button>
              <button
                onClick={() => dismiss(false)}
                data-testid="welcome-popup-thanks-close-button"
                className="text-[11px] tracking-[0.2em] uppercase text-foreground/40 hover:text-foreground transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
